import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getDashboardConfig, DashboardConfig } from '../api/onboarding';
import { useAuth } from './AuthContext';

interface DashboardContextType {
  config: DashboardConfig | null;
  loading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
  getTerminology: (key: string, fallback?: string) => string;
  isIndustrySpecific: boolean;
}

const defaultConfig: DashboardConfig = {
  industry: 'other',
  industryDisplayName: 'General',
  kpis: {
    primary: ['total_revenue', 'total_orders', 'aov', 'customer_count'],
    secondary: ['growth_rate', 'top_products', 'customer_retention'],
  },
  kpiDefinitions: {},
  terminology: {
    orders: 'Orders',
    customers: 'Customers',
    transactions: 'Transactions',
    revenue: 'Revenue',
    aov: 'Average Order Value',
  },
  suggestedIntegrations: [],
  icon: 'Business',
  color: '#64748B',
};

const DashboardContext = createContext<DashboardContextType>({
  config: null,
  loading: true,
  error: null,
  refreshConfig: async () => {},
  getTerminology: (key, fallback) => fallback || key,
  isIndustrySpecific: false,
});

export const useDashboardConfig = () => useContext(DashboardContext);

interface DashboardProviderProps {
  children: ReactNode;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [config, setConfig] = useState<DashboardConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    if (!isAuthenticated) {
      setConfig(defaultConfig);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getDashboardConfig();
      setConfig(data);
    } catch (err: any) {
      console.error('Error fetching dashboard config:', err);
      // Use default config on error
      setConfig(defaultConfig);
      setError(err?.message || 'Failed to load dashboard configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  // The config is loaded when the user signs in or out, which is the real
  // trigger. `fetchConfig` is redefined each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const refreshConfig = async () => {
    await fetchConfig();
  };

  const getTerminology = (key: string, fallback?: string): string => {
    if (!config?.terminology) return fallback || key;
    return config.terminology[key] || fallback || key;
  };

  const isIndustrySpecific = config?.industry !== 'other';

  return (
    <DashboardContext.Provider
      value={{
        config,
        loading,
        error,
        refreshConfig,
        getTerminology,
        isIndustrySpecific,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContext;
